/**
 * Density Persistence — scope-aware helper for persisting the user's density
 * preference (Library vs Gallery).
 *
 * Precedence: URL param `density` first, then localStorage (by scope), else
 * default 'comfortable'. URL state applies to the current render only; only
 * user interactions should persist preferences.
 */

import { COMFORTABLE, DENSITY, LOCAL_STORAGE_KEYS, VIEW_CONTEXT } from 'src/constants';
import { getCustomSearchURL } from 'src/helpers/filters';
import type { DensityType } from 'src/types/store.types';

type ViewContextScope = typeof VIEW_CONTEXT.LIBRARY | typeof VIEW_CONTEXT.GALLERY;

const VALID_DENSITIES = ['compact', 'comfortable', 'spacious'] as const;

const storageKeyForScope = (scope: ViewContextScope): string =>
  scope === VIEW_CONTEXT.GALLERY ? LOCAL_STORAGE_KEYS.GALLERY_DENSITY : LOCAL_STORAGE_KEYS.LIBRARY_DENSITY;

export const isValidDensity = (value: string): value is DensityType =>
  VALID_DENSITIES.includes(value as (typeof VALID_DENSITIES)[number]);

export const persistDensity = (scope: ViewContextScope, density: string): void => {
  if (!isValidDensity(density)) return;
  try {
    window.localStorage.setItem(storageKeyForScope(scope), density);
  } catch {
    // localStorage not available; ignore
  }
};

export const getPersistedDensity = (scope: ViewContextScope = VIEW_CONTEXT.LIBRARY): DensityType => {
  // 1) Check URL param first (URL overrides localStorage when explicitly present)
  const search = getCustomSearchURL();
  const urlDensity = search.get(DENSITY);
  if (urlDensity && isValidDensity(urlDensity)) {
    return urlDensity;
  }

  // 2) Try localStorage
  try {
    const stored = window.localStorage.getItem(storageKeyForScope(scope));
    if (stored && isValidDensity(stored)) return stored;
  } catch {
    // localStorage not available
  }

  // 3) Default
  return COMFORTABLE;
};

export const clearAllPersistedDensities = (): void => {
  try {
    window.localStorage.removeItem(LOCAL_STORAGE_KEYS.LIBRARY_DENSITY);
    window.localStorage.removeItem(LOCAL_STORAGE_KEYS.GALLERY_DENSITY);
  } catch {
    // localStorage not available; ignore
  }
};
